import { useEffect, useRef, useState } from 'react'
import type { RemoteTrack, Room, RoomOptions } from 'livekit-client'
import { mintFeedToken } from './api'
import { NO_MEDIA_SERVER } from './broadcastSession'
import { explain } from '@/shared/api/problem'

/**
 * The WebRTC layer, kept out of React on purpose.
 *
 * ## Why the SDK is imported dynamically
 *
 * `livekit-client` is ~250 KB of WebRTC machinery. The overwhelming majority of
 * sessions in this console never open a feed, and paying for it in the main
 * bundle to serve the minority is the same trade `mapbox-impl` already declined.
 * Every entry point below is `async` for that reason alone — `await import(...)`
 * is the first line of each, and the chunk arrives while the token request is in
 * flight, so it costs no wall clock.
 *
 * ## Why there is no "seconds watched" anywhere in this file
 *
 * Watch time is measured by the media server through `participant_joined` /
 * `participant_left` webhooks. There is no endpoint that accepts a duration and
 * there must not be one: a number the browser computes about the person holding
 * the browser is not evidence. If a future requirement wants finer detail than
 * join-to-leave, it comes from LiveKit, not from a `setInterval` here.
 */

/** Mirrors LiveKit's own connection lifecycle plus the two states it cannot know about. */
export type MediaState =
  | 'idle'
  /** Asking the backend for a credential. A refusal here is authorisation, not network. */
  | 'authorising'
  /** Have a token, opening the WebRTC connection. */
  | 'connecting'
  /** In the room, nobody is publishing yet. */
  | 'waiting'
  /**
   * The BACKEND refused the credential — hidden feed, camera held by somebody
   * else, event over. Separate from `failed` because the two need different
   * words and different actions: this one is never fixed by trying again.
   */
  | 'refused'
  /** Frames are arriving. */
  | 'live'
  /** The publisher left. Distinct from `failed`: nothing is broken. */
  | 'ended'
  | 'failed'
  /** This build has no media server configured — see `NO_MEDIA_SERVER`. */
  | 'unavailable'

/**
 * The token response carries the address the BROWSER should dial, which is not
 * the address the backend uses. A blank one means the deployment has no media
 * server reachable from here — the mock build is the everyday case — and the
 * honest thing is to say so rather than to spin on a WebSocket that will never
 * open. `axis.coverage.enabled=false` 503s before this, so these are different
 * failures and they get different words.
 */
async function connect(token: string, wsUrl: string, options?: RoomOptions): Promise<Room> {
  const { Room: LiveKitRoom } = await import('livekit-client')
  const room = new LiveKitRoom({ adaptiveStream: true, dynacast: true, ...options })
  await room.connect(wsUrl, token)
  return room
}

// ── watching ────────────────────────────────────────────────────────────────

/**
 * Subscribe to one feed.
 *
 * The `feedId` is the whole dependency. A re-render that changes a label must not
 * tear down a connection that took four seconds to establish, which is why every
 * other input is read through a ref.
 */
export function useCoverageViewer(feedId: string | null) {
  const [state, setState] = useState<MediaState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [participants, setParticipants] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const roomRef = useRef<Room | null>(null)

  useEffect(() => {
    if (!feedId) { setState('idle'); return }
    let stopped = false
    let room: Room | null = null

    const attach = (track: RemoteTrack) => {
      const el = videoRef.current
      if (el && (track.kind === 'video' || track.kind === 'audio')) track.attach(el)
    }

    ;(async () => {
      setState('authorising'); setError(null)
      try {
        const grant = await mintFeedToken(feedId, 'VIEWER')
        if (stopped) return
        if (!grant.wsUrl) { setState('unavailable'); setError(NO_MEDIA_SERVER); return }

        setState('connecting')
        const { RoomEvent } = await import('livekit-client')
        room = await connect(grant.token, grant.wsUrl)
        if (stopped) { await room.disconnect(); return }
        roomRef.current = room

        // `room.numParticipants` is the JOIN-TIME server snapshot and is never
        // recomputed on join/leave, so recounting from it returns the same number
        // forever. `remoteParticipants` is the live map — and it excludes us, so
        // it is already "everybody else" with no arithmetic.
        const recount = () => setParticipants(room ? room.remoteParticipants.size : 0)

        /** Is anybody still publishing? Not a headcount — a second VIEWER is not a camera. */
        const publishing = () => {
          if (!room) return false
          for (const [, part] of room.remoteParticipants) {
            for (const [, pub] of part.trackPublications) if (pub.track) return true
          }
          return false
        }

        room
          .on(RoomEvent.TrackSubscribed, (track) => { attach(track); recount(); setState('live') })
          .on(RoomEvent.TrackUnsubscribed, (track) => {
            track.detach()
            // Detaching leaves the last frame on screen. Without this the LIVE
            // badge sits over a frozen picture, which is the most misleading
            // state a live player can be in.
            if (!stopped && !publishing()) setState('waiting')
          })
          // A publisher leaving is not an error. PollSphere's player reports it
          // through the same amber "could not establish a connection" panel as a
          // failed ICE negotiation, which sends people to look at the network
          // when the answer is that the rally finished.
          .on(RoomEvent.ParticipantDisconnected, () => {
            recount()
            if (!stopped && !publishing()) setState('ended')
          })
          .on(RoomEvent.ParticipantConnected, recount)
          .on(RoomEvent.Disconnected, (reason) => {
            if (stopped) return
            // LiveKit hands us a reason, and they are not the same event. The
            // publisher finishing is not a fault; a server shutdown or a lost
            // transport is. Telling somebody the network broke when the rally
            // ended sends them to look in the wrong place.
            const clean = reason === undefined || reason === 1 /* CLIENT_INITIATED */
            setState(clean ? 'ended' : 'failed')
            if (!clean) setError('The connection to the media server was lost.')
          })
          .on(RoomEvent.Reconnecting, () => { if (!stopped) setState('connecting') })
          .on(RoomEvent.Reconnected, () => { if (!stopped) setState(publishing() ? 'live' : 'waiting') })

        recount()
        // Already-published tracks do not fire TrackSubscribed for a late joiner
        // in every SDK path, so adopt whatever is already there.
        let found = false
        room.remoteParticipants.forEach((p) => {
          p.trackPublications.forEach((pub) => {
            if (pub.track) { attach(pub.track as RemoteTrack); found = true }
          })
        })
        setState(found ? 'live' : 'waiting')
      } catch (e) {
        if (stopped) return
        // A throw BEFORE the room exists came from the backend refusing the
        // credential — 404 for a feed this caller may not see, 409 for a camera
        // somebody else holds. That is not a connection problem and must not be
        // worded as one.
        setState(room ? 'failed' : 'refused')
        setError(explain(e))
      }
    })()

    return () => {
      stopped = true
      const r = roomRef.current
      roomRef.current = null
      // Otherwise a stale last frame sits behind the next feed's overlay.
      if (videoRef.current) videoRef.current.srcObject = null
      void r?.disconnect()
    }
  }, [feedId])

  return { state, error, participants, videoRef }
}

// ── broadcasting ────────────────────────────────────────────────────────────
//
// It moved to `broadcastSession.ts`, and the move is the point: as component
// state, the cleanup disconnected the room on unmount, so navigating from Go
// Live to the diary took the camera off air. The session is now a module
// singleton and React subscribes to it — unmounting a panel detaches a video
// element, it does not end a broadcast.
export {
  preflight, openCamera, goLive, stop, dismiss, subscribe, getSnapshot,
  currentMedia, isBroadcasting, NO_MEDIA_SERVER,
  type BroadcastPhase, type BroadcastSnapshot,
} from './broadcastSession'
